/* PBC Promotion Popup MVP – no dependencies; one promotion at a time. */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-pbc-promo-root]';
  const cartRoot = () => (window.Shopify?.routes?.root || '/');
  const query = (el, selector) => el.querySelector(selector);

  function parseJSON(el) {
    try { return JSON.parse(el?.textContent || '{}'); }
    catch (error) { console.warn('[PBC Promo] Invalid Liquid JSON', error); return {}; }
  }

  function currentDateInZone(zone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: zone || 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date());
      const part = (type) => parts.find(p => p.type === type)?.value || '';
      return `${part('year')}-${part('month')}-${part('day')}`;
    } catch (error) {
      console.warn('[PBC Promo] Invalid time zone; using UTC', error);
      return new Date().toISOString().slice(0, 10);
    }
  }

  function isValidDay(day) { return /^\d{4}-\d{2}-\d{2}$/.test(day || ''); }

  function cartEditUrl(item) {
    const u = new URL(item.url || '/', window.location.origin);
    u.searchParams.set('item', item.key);
    return u.pathname + u.search + u.hash;
  }

  class PBCPromo {
    constructor(root) {
      this.root = root;
      this.settings = parseJSON(query(root, '[data-pbc-promo-settings]'));
      this.sectionId = root.dataset.sectionId;
      this.titleEl = query(root, '[data-pbc-promo-title]');
      this.descriptionEl = query(root, '[data-pbc-promo-description]');
      this.eligibleList = query(root, '[data-pbc-promo-eligible-items]');
      this.shopList = query(root, '[data-pbc-promo-shop-products]');
      this.mainButton = query(root, '[data-pbc-promo-primary]');
      this.errorEl = query(root, '[data-pbc-promo-error]');
      this.dialog = query(root, '[data-pbc-promo-dialog]');
      this.preview = Boolean(window.Shopify?.designMode && this.settings.previewInEditor);
      this.cartData = parseJSON(query(root, '[data-pbc-promo-cart]'));
      this.delayTimer = null;
      this.refreshTimer = null;
      this.refreshSequence = 0;
      this.isOpen = false;
      this.pageDismissed = false;
      this.previousFocus = null;
      this.savedBodyOverflow = null;
      this.bound = [];
      this.cartObserver = null;
      this.attach();
      this.refresh();
    }

    on(target, event, fn, options) {
      target.addEventListener(event, fn, options);
      this.bound.push([target, event, fn, options]);
    }

    attach() {
      this.on(this.root, 'click', (event) => {
        if (event.target.closest('[data-pbc-promo-close]')) this.close(true);
      });
      this.on(this.mainButton, 'click', (event) => this.onPrimary(event));
      this.on(document, 'keydown', (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') { event.preventDefault(); this.close(true); }
        if (event.key === 'Tab') this.trapFocus(event);
      });

      // Shopify now defines standard storefront events, but many Broadcast installs
      // do not yet dispatch all of them. Support both new and common theme events.
      for (const name of [
        'shopify:cart:lines-update', 'cart:updated', 'cart:refresh',
        'cart:change', 'ajaxProduct:added', 'pbc:cart-updated'
      ]) this.on(document, name, () => this.scheduleRefresh(150));

      // Fallbacks for conventional Add to Cart / cart quantity controls.
      this.on(document, 'submit', (event) => {
        if (event.target.matches('form[action*="/cart/add"]')) {
          this.scheduleRefresh(550);
          window.setTimeout(() => this.scheduleRefresh(0), 1700);
        }
      }, true);
      this.on(document, 'click', (event) => {
        if (event.target.closest('[name="add"], [data-add-to-cart], [data-product-submit], [data-cart-remove], [data-cart-remove-button], [data-quantity-plus], [data-quantity-minus]')) {
          this.scheduleRefresh(700);
          window.setTimeout(() => this.scheduleRefresh(0), 1500);
        }
      }, true);
      this.on(document, 'change', (event) => {
        if (event.target.matches('input[name="updates[]"], input[name="quantity"], [data-cart-quantity] input')) {
          this.scheduleRefresh(650);
          window.setTimeout(() => this.scheduleRefresh(0), 1500);
        }
      }, true);
      this.on(window, 'pageshow', () => this.scheduleRefresh(0));
      this.on(window, 'focus', () => this.scheduleRefresh(0));
      this.on(document, 'visibilitychange', () => {
        if (!document.hidden) this.scheduleRefresh(0);
      });

      // Broadcast 5.2 may replace the drawer without firing a standard cart event.
      // Only drawer mutations trigger refreshes; we never intercept fetch/XHR.
      if (document.body) {
        const cartSelector = '[data-cart-drawer], [data-drawer="cart"], #CartDrawer, #cart-drawer, .cart-drawer, .drawer--cart';
        this.cartObserver = new MutationObserver(mutations => {
          const changed = mutations.some(mutation => {
            const element = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
            if (!element || element.closest(ROOT_SELECTOR)) return false;
            if (element.closest(cartSelector)) return true;
            return Array.from(mutation.addedNodes).some(node =>
              node.nodeType === 1 && (node.matches(cartSelector) || node.querySelector(cartSelector)));
          });
          if (changed) this.scheduleRefresh(280);
        });
        this.cartObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    }

    destroy() {
      this.close(false);
      window.clearTimeout(this.delayTimer);
      window.clearTimeout(this.refreshTimer);
      this.cartObserver?.disconnect();
      this.bound.forEach(([target, event, fn, options]) => target.removeEventListener(event, fn, options));
      this.bound = [];
      if (this.root._pbcPromoInstance === this) delete this.root._pbcPromoInstance;
    }

    storageKey() {
      return `pbc-promo-mvp:${this.settings.campaignKey || this.sectionId}`;
    }
    wasDismissed() {
      if (this.pageDismissed) return true;
      if (this.preview || this.settings.frequency === 'always') return false;
      try {
        const storage = this.settings.frequency === 'day' ? window.localStorage : window.sessionStorage;
        const val = storage.getItem(this.storageKey());
        return this.settings.frequency === 'day'
          ? val === currentDateInZone(this.settings.timeZone)
          : val === 'dismissed';
      } catch (_error) { return false; }
    }
    markDismissed() {
      this.pageDismissed = true;
      if (this.preview || this.settings.frequency === 'always') return;
      try {
        const storage = this.settings.frequency === 'day' ? window.localStorage : window.sessionStorage;
        storage.setItem(this.storageKey(), this.settings.frequency === 'day'
          ? currentDateInZone(this.settings.timeZone) : 'dismissed');
      } catch (_error) { /* Storage may be blocked. Page guard still works. */ }
    }

    scheduleRefresh(delay = 0) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), delay);
    }

    async refresh() {
      if (!this.root.isConnected) return;
      const n = ++this.refreshSequence;
      try {
        const url = `${cartRoot()}cart?section_id=${encodeURIComponent(this.sectionId)}&_=${Date.now()}`;
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(`Section request returned ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const script = doc.querySelector('[data-pbc-promo-cart]');
        if (!script) throw new Error('Cart JSON not present in rendered section');
        if (n !== this.refreshSequence || !this.root.isConnected) return;
        this.cartData = parseJSON(script);
        this.evaluate();
      } catch (error) {
        console.warn('[PBC Promo] Cart refresh failed; using initial state', error);
        if (n === this.refreshSequence) this.evaluate();
      }
    }

    withinSchedule() {
      if (this.preview) return true;
      if (!this.settings.enabled) return false;
      const { startDate, endDate, timeZone } = this.settings;
      // Fail closed if an admin enters a malformed schedule.
      if (!isValidDay(startDate) || !isValidDay(endDate) || startDate > endDate) return false;
      const today = currentDateInZone(timeZone);
      return today >= startDate && today <= endDate;
    }

    decideState() {
      if (!this.withinSchedule()) return null;
      if (!this.preview) {
        if (this.settings.mode === 'embroidery' &&
            (!this.settings.shopUrl || !this.settings.addonProductId)) return null;
        if (this.settings.mode === 'simple' && !this.settings.simpleProductUrl) return null;
      }
      const items = this.cartData.items || [];
      const addonPresent = items.some(i => i.isAddon && i.quantity > 0);
      const personalizedPresent = items.some(i => i.personalized && i.quantity > 0);
      if (this.settings.mode === 'embroidery' && (addonPresent || personalizedPresent) && !this.preview) return null;

      // 'Customer buys' discount condition is on product's undiscounted prices.
      // Embroidery must not contribute to the spend threshold.
      // Every product except the selected Embroidery add-on counts.
      const subtotal = items.reduce((sum, item) =>
        sum + (item.isAddon ? 0 : Number(item.originalLineCents || 0)), 0);
      const required = Number(this.settings.minimumCents || 0);
      if (subtotal < required && !this.preview) return null;

      if (this.settings.mode === 'simple') return { kind: 'simple' };
      const eligible = items.filter(i =>
        i.collectionEligible && i.thqEligible && !i.personalized && !i.isAddon && i.quantity > 0
      );
      return eligible.length ? { kind: 'eligible', items: eligible } : { kind: 'shop' };
    }

    evaluate() {
      const state = this.decideState();
      window.clearTimeout(this.delayTimer);
      if (!state || this.wasDismissed()) {
        if (this.isOpen) this.close(false);
        return;
      }
      this.renderState(state);
      if (!this.isOpen) {
        this.delayTimer = window.setTimeout(() => {
          if (this.wasDismissed() || !this.decideState()) return;
          this.open();
        }, this.preview ? 0 : Number(this.settings.delaySeconds || 0) * 1000);
      }
    }

    renderState(state) {
      const s = this.settings;
      this.eligibleList.hidden = true;
      this.shopList.hidden = true;
      this.mainButton.hidden = true;
      this.errorEl.hidden = true;
      this.eligibleList.replaceChildren();
      this.mainButton.removeAttribute('data-add-to-cart');

      if (state.kind === 'eligible') {
        this.titleEl.textContent = s.titleEligible;
        this.descriptionEl.textContent = s.textEligible;
        this.eligibleList.hidden = false;
        const note = document.createElement('p');
        note.className = 'pbc-promo__item-note';
        note.textContent = state.items.length > 1 ? 'Choose an item to personalize:' : '';
        this.eligibleList.append(note);
        state.items.forEach(item => {
          const row = document.createElement('div');
          row.className = 'pbc-promo__cart-row';
          const details = document.createElement('div');
          details.className = 'pbc-promo__cart-details';
          if (item.imageUrl) {
            const image = document.createElement('img');
            image.className = 'pbc-promo__thumb pbc-promo__cart-thumb';
            image.src = item.imageUrl;
            image.alt = ''; // Product name is displayed immediately beside the image.
            image.width = 64;
            image.height = 64;
            image.loading = 'eager';
            image.decoding = 'async';
            details.append(image);
          }
          const itemName = document.createElement('span');
          itemName.className = 'pbc-promo__cart-name';
          itemName.textContent = `${item.name}${item.variantName && item.variantName !== 'Default Title' ? ' — ' + item.variantName : ''}`;
          details.append(itemName);
          const link = document.createElement('a');
          link.className = 'pbc-promo__cart-action';
          if (item.quantity > 1) {
            link.href = s.cartUrl || `${cartRoot()}cart`;
            link.textContent = 'Adjust quantity first →';
            link.title = 'For one free embroidery, split this cart line so its quantity is 1.';
          } else {
            link.href = cartEditUrl(item);
            link.textContent = `${s.buttonEligible} →`;
          }
          link.addEventListener('click', () => this.markDismissed());
          row.append(details, link);
          this.eligibleList.append(row);
        });
      } else if (state.kind === 'shop') {
        this.titleEl.textContent = s.titleShop;
        this.descriptionEl.textContent = s.textShop;
        this.shopList.hidden = !this.shopList.querySelector('.pbc-promo__product');
        if (s.shopUrl) {
          this.mainButton.href = s.shopUrl;
          this.mainButton.textContent = s.buttonShop;
          this.mainButton.hidden = false;
        }
      } else {
        this.titleEl.textContent = s.titleSimple;
        this.descriptionEl.textContent = s.textSimple;
        if (s.simpleProductUrl) {
          this.mainButton.href = s.simpleProductUrl;
          this.mainButton.textContent = s.buttonSimple;
          this.mainButton.hidden = false;
          if (s.simpleAction === 'add' && s.simpleVariantId) {
            this.mainButton.dataset.addToCart = s.simpleVariantId;
          }
        }
      }
    }

    async onPrimary(event) {
      if (this.mainButton.hidden) return;
      const variantId = this.mainButton.dataset.addToCart;
      if (!variantId) { this.markDismissed(); return; }
      event.preventDefault();
      this.mainButton.setAttribute('aria-disabled', 'true');
      try {
        const response = await fetch(`${cartRoot()}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Could not add that product. Please open its product page.');
        this.markDismissed();
        // Reliable theme-independent fallback: show the updated cart, not a stale drawer.
        window.location.assign(this.settings.cartUrl || `${cartRoot()}cart`);
      } catch (error) {
        this.errorEl.textContent = error.message;
        this.errorEl.hidden = false;
        this.mainButton.removeAttribute('aria-disabled');
      }
    }

    open() {
      if (this.isOpen) return;
      this.previousFocus = document.activeElement;
      this.savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      this.root.hidden = false;
      this.dialog.focus();
    }

    close(userDismissed) {
      window.clearTimeout(this.delayTimer);
      if (userDismissed) this.markDismissed();
      if (!this.isOpen) { this.root.hidden = true; return; }
      this.isOpen = false;
      this.root.hidden = true;
      document.body.style.overflow = this.savedBodyOverflow || '';
      this.previousFocus?.focus?.({ preventScroll: true });
    }

    trapFocus(event) {
      const focusable = Array.from(this.dialog.querySelectorAll(
        'a[href]:not([hidden]), button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(el => !el.closest('[hidden]'));
      if (!focusable.length) { event.preventDefault(); this.dialog.focus(); return; }
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
  }

  function initialize() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      if (!root._pbcPromoInstance) root._pbcPromoInstance = new PBCPromo(root);
    });
  }
  function destroyRemoved() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      if (root._pbcPromoInstance && !root._pbcPromoInstance.root.isConnected)
        root._pbcPromoInstance.destroy();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  document.addEventListener('shopify:section:load', () => { destroyRemoved(); initialize(); });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach(root => root._pbcPromoInstance?.destroy());
  });

  // Handy during testing: document.dispatchEvent(new Event('pbc:cart-updated'))
  window.PBCPromoPopup = { refresh: () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => root._pbcPromoInstance?.scheduleRefresh(0));
  }};
})();